import React from "react";
import { QuestionMarkCircleIcon, ExclamationTriangleIcon, QrCodeIcon } from "@heroicons/react/24/outline";

import useOrderFulfillment from "./hooks/useOrderFulfillment";

import OrdersList from "./components/OrdersList";
import OrderHeaderActions from "./components/OrderHeaderActions";
import OrderDetailsGrid from "./components/OrderDetailsGrid";
import OrderItemsList from "./components/OrderItemsList";
import FulfillmentModals from "./components/FulfillmentModals";

export default function OrderFulfillment() {
    const {
        mode,
        handleAllOrdersClick,
        handleBackClick,
        orders,
        orderQuery,
        setOrderQuery,
        debouncedOrderQuery,
        setDebouncedOrderQuery,
        allOrdersQuery,
        setAllOrdersQuery,
        debouncedAllOrdersQuery,
        setDebouncedAllOrdersQuery,
        loadingOrders,
        loadingMoreOrders,
        statusCounts,
        countsLoaded,
        listContainerRef,
        loadingDetails,
        detailsError,
        cartLoading,
        cartError,
        cancelingOrder,
        memberLoading,
        memberInfo,
        selectedOrder,
        toast,
        showDamageModal,
        setShowDamageModal,
        showCancelModal,
        setShowCancelModal,
        damageSubmitting,
        setDamageRemoveFromOrder,
        damageForm,
        setDamageForm,
        actionSubmitting,
        replacingItem,
        setReplacingItem,
        damageTargetItem,
        copyOptions,
        copiesLoading,
        showScanReplaceModal,
        setShowScanReplaceModal,
        scannedReplaceId,
        setScannedReplaceId,
        scanningReplace,
        scanReplaceError,
        activeLeftDropdown,
        setActiveLeftDropdown,
        activeStatusFilter,
        setActiveStatusFilter,
        selectOrder,
        handleRemoveFromDelivery,
        triggerCancelConfirm,
        confirmCancelOrder,
        handleCardAction,
        handleStartScanReplace,
        handleScanReplaceSubmit,
        handleDamageSubmit,
        handleSubmitAction,
        handleClearActions,
        handleStatusChange,
        orderStatuses,
        statusesLoading,
        statusesError,
        copySearchQuery,
        setCopySearchQuery,
        copySearchLoading,
        handleManualAddBook,
        searchResults,
        searchLoading,
        showDropdown,
        setShowDropdown,
        selectedBookFromSearch,
        setSelectedBookFromSearch,
        packedItems
    } = useOrderFulfillment();

    const searchContainerRef = React.useRef(null);
    React.useEffect(() => {
        function handleClickOutside(event) {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [setShowDropdown]);

    return (
        <div className="flex h-screen w-full bg-white overflow-hidden font-sans">
            {/* Toast Notification */}
            {toast && (
                <div className="fixed top-5 right-5 z-[100] bg-gray-900 text-white text-sm px-4 py-3 rounded-lg shadow-lg font-bold">
                    {toast}
                </div>
            )}

            {/* === LEFT PANEL: ORDERS LIST === */}
            <OrdersList
                mode={mode}
                handleAllOrdersClick={handleAllOrdersClick}
                handleBackClick={handleBackClick}
                orders={orders}
                orderQuery={orderQuery}
                setOrderQuery={setOrderQuery}
                debouncedOrderQuery={debouncedOrderQuery}
                setDebouncedOrderQuery={setDebouncedOrderQuery}
                allOrdersQuery={allOrdersQuery}
                setAllOrdersQuery={setAllOrdersQuery}
                debouncedAllOrdersQuery={debouncedAllOrdersQuery}
                setDebouncedAllOrdersQuery={setDebouncedAllOrdersQuery}
                loadingOrders={loadingOrders}
                loadingDetails={loadingDetails}
                loadingMoreOrders={loadingMoreOrders}
                statusCounts={statusCounts}
                countsLoaded={countsLoaded}
                listContainerRef={listContainerRef}
                activeStatusFilter={activeStatusFilter}
                setActiveStatusFilter={setActiveStatusFilter}
                selectedOrder={selectedOrder}
                selectOrder={selectOrder}
                activeLeftDropdown={activeLeftDropdown}
                setActiveLeftDropdown={setActiveLeftDropdown}
                actionSubmitting={actionSubmitting}
                setDamageRemoveFromOrder={setDamageRemoveFromOrder}
                handleCardAction={handleCardAction}
                handleStartScanReplace={handleStartScanReplace}
                packedItems={packedItems}
            />

            {/* === RIGHT PANEL: FULFILLMENT & ORDER DETAILS === */}
            <div className="flex-1 h-full overflow-y-auto bg-gray-50/50 p-6">
                {!selectedOrder ? (
                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 py-20 bg-white border border-gray-150 rounded-lg shadow-xs">
                        <QuestionMarkCircleIcon className="h-12 w-12 text-gray-300 stroke-[1.5]" />
                        <p className="text-sm font-semibold uppercase tracking-wider mt-4">
                            No Order Selected
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                            Search an order number or select a book to view order details
                        </p>
                    </div>
                ) : detailsError ? (
                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 py-20 bg-white border border-gray-150 rounded-lg shadow-xs px-6 text-center">
                        <ExclamationTriangleIcon className="h-12 w-12 text-amber-500 stroke-[1.5]" />
                        <p className="text-sm font-semibold uppercase tracking-wider mt-4 text-gray-800">
                            Fulfillment Warning
                        </p>
                        <p className="text-xs text-gray-500 mt-2 max-w-md">
                            {detailsError}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-6 max-w-6xl mx-auto">
                        {/* 1. Header & Actions Row */}
                        <OrderHeaderActions
                            selectedOrder={selectedOrder}
                            cancelingOrder={cancelingOrder}
                            actionSubmitting={actionSubmitting}
                            triggerCancelConfirm={triggerCancelConfirm}
                            onStatusChange={handleStatusChange}
                            orderStatuses={orderStatuses}
                            statusesLoading={statusesLoading}
                            statusesError={statusesError}
                        />

                        {/* 2. Grid for Member, Payment, Shipping, Slot, and Order Details */}
                        <OrderDetailsGrid
                            selectedOrder={selectedOrder}
                            loadingDetails={loadingDetails}
                            memberLoading={memberLoading}
                            memberInfo={memberInfo}
                        />

                        {/* 3. Manual Add Book Section */}
                        <div className="bg-white border border-gray-200 rounded-lg shadow-xs mt-6 text-left relative">
                            <div className="p-5 space-y-4">
                                <form onSubmit={handleManualAddBook} className="flex flex-col sm:flex-row gap-3 max-w-xl">
                                    <div className="flex-1 relative" ref={searchContainerRef}>
                                        <div
                                            className="absolute inset-y-0 left-0 pl-3 flex items-center cursor-pointer text-gray-400 hover:text-black transition-colors"
                                            onClick={() => {
                                                const firstItem = selectedOrder?.items?.[0] || { product_id: "", name: "" };
                                                handleStartScanReplace(firstItem);
                                            }}
                                            title="Scan Copy ID"
                                        >
                                            <QrCodeIcon className="h-5 w-5 stroke-[2]" />
                                        </div>
                                        <input
                                            type="text"
                                            value={copySearchQuery}
                                            onChange={(e) => {
                                                setCopySearchQuery(e.target.value);
                                                if (selectedBookFromSearch) {
                                                    setSelectedBookFromSearch(null);
                                                }
                                            }}
                                            onFocus={() => setShowDropdown(true)}
                                            placeholder="Scan / Enter Copy ID or Search Book"
                                            className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2 text-sm outline-none focus:ring-1 focus:ring-black focus:border-black font-semibold text-gray-800 bg-white"
                                            disabled={copySearchLoading}
                                        />

                                        {/* Dropdown search results */}
                                        {showDropdown && copySearchQuery.trim().length >= 2 && (
                                            <div className="absolute z-[9999] left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto divide-y divide-gray-150">
                                                {searchLoading ? (
                                                    <div className="p-4 text-center text-xs text-gray-500 font-bold flex items-center justify-center gap-2">
                                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black" />
                                                        <span>Searching books...</span>
                                                    </div>
                                                ) : searchResults.length === 0 ? (
                                                    <div className="p-4 text-center text-xs text-gray-400 font-semibold">
                                                        No books found
                                                    </div>
                                                ) : (
                                                    searchResults.map((book) => {
                                                        const bookImg = book.image || book.img || "https://via.placeholder.com/80x120?text=Cover";
                                                        const bookName = book.name || book.title || "Unknown Book";
                                                        const bookId = book.id || "N/A";
                                                        const bookCredits = book.price ?? book.credits ?? 0;
                                                        const rawStock = book.stock_status || "instock";
                                                        const isOutOfStock = String(rawStock).toLowerCase() === "outofstock" || String(rawStock).toLowerCase() === "out of stock";
                                                        const stockText = isOutOfStock ? "Out of stock" : "Available";

                                                        return (
                                                            <div
                                                                key={bookId}
                                                                onClick={() => {
                                                                    setSelectedBookFromSearch(book);
                                                                    setCopySearchQuery(bookName);
                                                                    setShowDropdown(false);
                                                                }}
                                                                className="p-3 hover:bg-gray-50 flex gap-3 items-center cursor-pointer transition-colors"
                                                            >
                                                                <img
                                                                    src={bookImg}
                                                                    alt={bookName}
                                                                    className="w-10 h-14 object-cover rounded border border-gray-150 flex-shrink-0"
                                                                />
                                                                <div className="min-w-0 flex-1 text-left">
                                                                    <h4 className="font-bold text-xs text-gray-900 truncate">
                                                                        {bookName}
                                                                    </h4>
                                                                    <p className="text-[10px] text-gray-500 mt-0.5">
                                                                        Product ID: <span className="font-semibold">{bookId}</span>
                                                                    </p>
                                                                    <p className="text-[10px] text-blue-600 font-bold mt-0.5">
                                                                        {bookCredits} Credit{Number(bookCredits) !== 1 ? "s" : ""}
                                                                    </p>
                                                                    <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-extrabold uppercase mt-1 ${isOutOfStock
                                                                        ? "bg-red-50 text-red-600 border border-red-100"
                                                                        : "bg-green-50 text-green-700 border border-green-150"
                                                                        }`}>
                                                                        {stockText}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={copySearchLoading || !copySearchQuery.trim()}
                                        className="px-6 py-2 bg-black text-white rounded-lg text-sm font-bold transition active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
                                    >
                                        {copySearchLoading ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                                                <span>Adding...</span>
                                            </>
                                        ) : (
                                            <span>Add Book</span>
                                        )}
                                    </button>
                                </form>
                            </div>
                        </div>

                        <OrderItemsList
                            items={packedItems}
                            selectedOrder={selectedOrder}
                            cartLoading={cartLoading}
                            cartError={cartError}
                            actionSubmitting={actionSubmitting}
                            handleRemoveFromDelivery={handleRemoveFromDelivery}
                            setDamageRemoveFromOrder={setDamageRemoveFromOrder}
                            handleCardAction={handleCardAction}
                            handleClearActions={handleClearActions}
                            handleSubmitAction={handleSubmitAction}
                        />

                    </div>
                )}
            </div>

            {/* === MODALS CONTAINER === */}
            <FulfillmentModals
                showDamageModal={showDamageModal}
                setShowDamageModal={setShowDamageModal}
                damageSubmitting={damageSubmitting}
                damageForm={damageForm}
                setDamageForm={setDamageForm}
                handleDamageSubmit={handleDamageSubmit}
                replacingItem={replacingItem}
                setReplacingItem={setReplacingItem}
                showScanReplaceModal={showScanReplaceModal}
                setShowScanReplaceModal={setShowScanReplaceModal}
                scannedReplaceId={scannedReplaceId}
                setScannedReplaceId={setScannedReplaceId}
                scanningReplace={scanningReplace}
                scanReplaceError={scanReplaceError}
                handleScanReplaceSubmit={handleScanReplaceSubmit}
                showCancelModal={showCancelModal}
                setShowCancelModal={setShowCancelModal}
                cancelingOrder={cancelingOrder}
                confirmCancelOrder={confirmCancelOrder}
                damageTargetItem={damageTargetItem}
                copyOptions={copyOptions}
                copiesLoading={copiesLoading}
            />
        </div>
    );
}